import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { getBoardSize } from '../../utils/gameLayout';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface Cell {
  letter: string | null;
  number: number | null;
}

interface DuetoBoard {
  puzzle: Cell[][];
  solution: Cell[][];
  seed: number;
  size: number;
  clues: number;
  empty: number;
}

export default function DuetoGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [board, setBoard] = useState<Cell[][] | null>(null);
  const [solution, setSolution] = useState<Cell[][] | null>(null);
  const [initialPuzzle, setInitialPuzzle] = useState<Cell[][] | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalFixedLetter, setModalFixedLetter] = useState<string | null>(null);
  const [modalFixedNumber, setModalFixedNumber] = useState<number | null>(null);
  const [modalCurrentLetter, setModalCurrentLetter] = useState<string | null>(null);
  const [modalCurrentNumber, setModalCurrentNumber] = useState<number | null>(null);
  const [undoStack, setUndoStack] = useState<Cell[][][]>([]);
  const [redoStack, setRedoStack] = useState<Cell[][][]>([]);
  const [startTime] = useState(Date.now());
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hint, setHint] = useState<{ row: number; col: number } | null>(null);
  const [currentSeed, setCurrentSeed] = useState<number | null>(null);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('dueto', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Cargar puzzle
  useEffect(() => {
    loadPuzzle();
  }, [dificultadOrden]);

  const loadPuzzle = async (forceNew = false) => {
    try {
      setLoading(true);
      setError('');

      // Si forceNew es true, ignorar URL seed y generar nuevo
      const urlSeed = !forceNew ? searchParams.get('seed') : null;
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;

      const response = await api.get('/dueto/generate', {
        params: {
          dificultad: dificultadOrden,
          ...(seedToUse && { seed: seedToUse })
        }
      });

      const data: DuetoBoard = response.data.data;
      setBoard(data.puzzle);
      setSolution(data.solution);
      setInitialPuzzle(JSON.parse(JSON.stringify(data.puzzle)));
      setCurrentSeed(data.seed);
      setUndoStack([]);
      setRedoStack([]);
      setIsCompleted(false);
      setShowVictory(false);
      setHint(null);

      // Actualizar URL con el nuevo seed
      const newUrl = `${window.location.pathname}?seed=${data.seed}`;
      window.history.replaceState({}, '', newUrl);
    } catch (err: any) {
      console.error('Error al cargar puzzle:', err);
      setError(err.response?.data?.error || 'Error al cargar el puzzle');
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal para seleccionar letra y número
  const handleCellClick = (row: number, col: number) => {
    if (!board || isCompleted) return;

    // Bloquear celdas completamente dadas por el sistema
    if (initialPuzzle &&
        initialPuzzle[row][col].letter !== null &&
        initialPuzzle[row][col].number !== null) {
      return;
    }

    const initCell = initialPuzzle?.[row][col];
    const currentCell = board[row][col];

    // Parte fija por el sistema (no editable)
    setModalFixedLetter(initCell?.letter ?? null);
    setModalFixedNumber(initCell?.number ?? null);
    // Entrada actual del usuario (editable, puede ser parcial)
    setModalCurrentLetter(initCell?.letter !== null ? null : currentCell.letter);
    setModalCurrentNumber(initCell?.number !== null ? null : currentCell.number);
    setSelectedCell({ row, col });
    setShowModal(true);
    setHint(null);
  };

  // Colocar valor (puede ser parcial: solo letra, solo número, o ambos)
  const submitValue = async (letter: string | null, number: number | null) => {
    if (!board || !selectedCell) return;

    const { row, col } = selectedCell;
    pushUndoState();

    // Validar contra API solo cuando el par está completo
    if (letter !== null && number !== null) {
      try {
        await api.post('/dueto/validate', { board, row, col, letter, number });
      } catch (err: any) {
        console.error('Error al validar:', err);
        setError('Error al validar el movimiento');
        return;
      }
    }

    const newBoard = board.map((r, i) =>
      r.map((cell, j) =>
        i === row && j === col ? { letter, number } : cell
      )
    );

    setBoard(newBoard);
    setShowModal(false);
    setSelectedCell(null);

    if (isBoardComplete(newBoard)) {
      await checkVictory(newBoard);
    }
  };

  // Verificar si el tablero está completo
  const isBoardComplete = (boardToCheck: Cell[][]): boolean => {
    for (const row of boardToCheck) {
      for (const cell of row) {
        if (cell.letter === null || cell.number === null) {
          return false;
        }
      }
    }
    return true;
  };

  // Verificar victoria
  const checkVictory = async (boardToCheck: Cell[][]) => {
    if (!solution) return;

    const n = boardToCheck.length;
    let isCorrect = true;

    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (boardToCheck[i][j].letter !== solution[i][j].letter ||
            boardToCheck[i][j].number !== solution[i][j].number) {
          isCorrect = false;
          break;
        }
      }
      if (!isCorrect) break;
    }

    if (isCorrect) {
      setIsCompleted(true);
      const tiempo = Math.floor((Date.now() - startTime) / 1000);
      await saveProgress(tiempo);
      setShowVictory(true);
    } else {
      setError('El tablero está completo pero hay errores');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Guardar progreso
  const saveProgress = async (tiempo: number) => {
    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: Date.now(),
        tiempo,
        movimientos: undoStack.length,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos: undoStack.length,
        completado: true,
        datos_juego: {
          tablero_id: currentSeed || Date.now(),
          size: board?.length || 0,
        },
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  // Undo/Redo
  const pushUndoState = () => {
    if (!board) return;
    setUndoStack([...undoStack, JSON.parse(JSON.stringify(board))]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length === 0 || !board) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack([...redoStack, JSON.parse(JSON.stringify(board))]);
    setBoard(prev);
    setUndoStack(undoStack.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0 || !board) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack([...undoStack, JSON.parse(JSON.stringify(board))]);
    setBoard(next);
    setRedoStack(redoStack.slice(0, -1));
  };

  // Obtener pista
  const handleHint = async () => {
    if (!board || !solution) return;

    try {
      const response = await api.post('/dueto/hint', {
        puzzle: board,
        solution
      });

      const hintData = response.data.data.hint;
      if (hintData) {
        setHint({ row: hintData.row, col: hintData.col });

        // Auto-rellenar la pista
        pushUndoState();
        const newBoard = board.map((r, i) =>
          r.map((cell, j) =>
            i === hintData.row && j === hintData.col
              ? { letter: hintData.letter, number: hintData.number }
              : cell
          )
        );
        setBoard(newBoard);

        // Verificar si está completo
        if (isBoardComplete(newBoard)) {
          await checkVictory(newBoard);
        }

        setTimeout(() => setHint(null), 2000);
      }
    } catch (err: any) {
      console.error('Error al obtener pista:', err);
    }
  };

  // Ver solución
  const showSolution = () => {
    if (!solution || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;
    setBoard(solution);
    setIsCompleted(true);
  };

  // Reiniciar al puzzle inicial
  const handleReset = () => {
    if (!initialPuzzle || !window.confirm('¿Estás seguro de que quieres reiniciar?')) return;
    setBoard(JSON.parse(JSON.stringify(initialPuzzle)));
    setUndoStack([]);
    setRedoStack([]);
    setIsCompleted(false);
    setShowVictory(false);
    setHint(null);
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(b: Cell[][]): Set<string> {
    const errores = new Set<string>();
    const n = b.length;

    // Duplicados de letra en fila
    for (let r = 0; r < n; r++) {
      const seen = new Map<string, number>();
      for (let c = 0; c < n; c++) {
        const l = b[r][c].letter;
        if (!l) continue;
        if (seen.has(l)) { errores.add(`${r},${seen.get(l)!}`); errores.add(`${r},${c}`); }
        else seen.set(l, c);
      }
    }
    // Duplicados de letra en columna
    for (let c = 0; c < n; c++) {
      const seen = new Map<string, number>();
      for (let r = 0; r < n; r++) {
        const l = b[r][c].letter;
        if (!l) continue;
        if (seen.has(l)) { errores.add(`${seen.get(l)!},${c}`); errores.add(`${r},${c}`); }
        else seen.set(l, r);
      }
    }
    // Duplicados de número en fila
    for (let r = 0; r < n; r++) {
      const seen = new Map<number, number>();
      for (let c = 0; c < n; c++) {
        const num = b[r][c].number;
        if (num === null) continue;
        if (seen.has(num)) { errores.add(`${r},${seen.get(num)!}`); errores.add(`${r},${c}`); }
        else seen.set(num, c);
      }
    }
    // Duplicados de número en columna
    for (let c = 0; c < n; c++) {
      const seen = new Map<number, number>();
      for (let r = 0; r < n; r++) {
        const num = b[r][c].number;
        if (num === null) continue;
        if (seen.has(num)) { errores.add(`${seen.get(num)!},${c}`); errores.add(`${r},${c}`); }
        else seen.set(num, r);
      }
    }
    // Combinaciones letra+número únicas en todo el tablero
    const seenCombos = new Map<string, [number, number]>();
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        const { letter: l, number: num } = b[r][c];
        if (!l || num === null) continue;
        const key = `${l}${num}`;
        if (seenCombos.has(key)) {
          const [pr, pc] = seenCombos.get(key)!;
          errores.add(`${pr},${pc}`);
          errores.add(`${r},${c}`);
        } else {
          seenCombos.set(key, [r, c]);
        }
      }
    }

    return errores;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  const size = board.length;

  // Fuente basada en la dimensión más pequeña de la celda (alto en landscape)
  const fontSize = `calc((100vh - 110px) / ${size} * 0.38)`;

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full overflow-hidden bg-gradient-to-br from-purple-900 to-indigo-900 flex flex-col">
      {/* Banner de Sesión */}
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🎭 Dueto</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => navigate(`/juegos/dueto/${d.orden}`)}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden ? 'bg-white text-purple-900' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={handleUndo} disabled={undoStack.length === 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={handleRedo} disabled={redoStack.length === 0} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={showSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={() => loadPuzzle(true)} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          {/* How to play */}
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-red-100 border border-red-300 text-red-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Board area */}
      <div className="flex-1 min-h-0 p-2 overflow-hidden">
        <div
          className="grid gap-1 bg-gray-300 p-1 rounded-lg"
          style={{
            gridTemplateColumns: `repeat(${size}, 1fr)`,
            gridTemplateRows: `repeat(${size}, 1fr)`,
            width: '100%',
            height: '100%',
          }}
        >
          {(() => {
            const errores = modoAsistido ? calcularErrores(board) : new Set<string>();
            return board.map((row, i) =>
              row.map((cell, j) => {
                const initCell = initialPuzzle?.[i][j];
                const isInitialFull = initCell?.letter !== null && initCell?.number !== null && initCell?.letter !== undefined;
                const isInitialPartial = !isInitialFull && (initCell?.letter !== null || initCell?.number !== null) && initCell !== undefined;
                const isInitialAny = isInitialFull || isInitialPartial;
                const isUserFull = !isInitialAny && cell.letter !== null && cell.number !== null;
                const isUserPartial = !isInitialAny && !isUserFull && (cell.letter !== null || cell.number !== null);
                const isHinted = hint && hint.row === i && hint.col === j;
                const hasError = errores.has(`${i},${j}`);

                const bgColor = hasError ? '#fecaca'
                  : isHinted ? '#fef08a'
                  : isInitialAny ? '#fde68a'
                  : isUserFull ? '#bbf7d0'
                  : isUserPartial ? '#dcfce7'
                  : '#ffffff';

                const borderStyle = isInitialFull
                  ? '3px solid #b45309'
                  : isInitialPartial
                  ? '3px solid #d97706'
                  : hasError ? '2px solid #ef4444'
                  : '1px solid #d1d5db';

                // Colores del texto por origen
                const letterColor = hasError ? '#dc2626'
                  : initCell?.letter !== null ? '#92400e'
                  : '#166534';
                const numberColor = hasError ? '#dc2626'
                  : initCell?.number !== null ? '#92400e'
                  : '#166534';

                return (
                  <button
                    key={`${i}-${j}`}
                    onClick={() => handleCellClick(i, j)}
                    disabled={isInitialFull || isCompleted}
                    className="flex items-center justify-center font-bold transition-all rounded"
                    style={{ fontSize, backgroundColor: bgColor, border: borderStyle,
                      cursor: (isInitialFull || isCompleted) ? 'not-allowed' : 'pointer' }}
                  >
                    {cell.letter === null && cell.number === null ? null
                      : cell.letter !== null && cell.number !== null ? (
                        // Par completo: letra+número juntos
                        <span style={{ color: letterColor }}>
                          {cell.letter}{cell.number}
                        </span>
                      ) : cell.letter !== null ? (
                        // Solo letra
                        <span style={{ color: letterColor }}>{cell.letter}</span>
                      ) : (
                        // Solo número
                        <span style={{ color: numberColor }}>{cell.number}</span>
                      )
                    }
                  </button>
                );
              })
            );
          })()}
        </div>
      </div>

      {/* Modal de entrada */}
      {showModal && selectedCell && (
        <InputModal
          size={size}
          fixedLetter={modalFixedLetter}
          fixedNumber={modalFixedNumber}
          currentLetter={modalCurrentLetter}
          currentNumber={modalCurrentNumber}
          onSubmit={submitValue}
          onClose={() => {
            setShowModal(false);
            setSelectedCell(null);
          }}
        />
      )}

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Dueto"
        gameIcon="🎭"
        objective="Coloca una letra y un número en cada casilla del tablero N×N sin repetir letras ni números en filas o columnas, y sin repetir ninguna combinación letra+número."
        rules={[
          'Cada letra (A, B, C…) debe aparecer exactamente una vez en cada fila y una vez en cada columna',
          'Cada número (1, 2, 3…) debe aparecer exactamente una vez en cada fila y una vez en cada columna',
          'Todas las combinaciones letra+número del tablero deben ser únicas: no puede haber dos celdas con el mismo par (ej. A2)',
        ]}
        howToPlay="Pulsa una celda vacía para seleccionar la letra y el número que quieres colocar. Las celdas dadas (en verde) no se pueden cambiar."
        author=""
      />

      {/* Modal de Victoria */}
      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: undoStack.length,
        }}
      />
    </div>
  );
}

// Componente modal para ingresar letra y/o número (admite entradas parciales)
function InputModal({
  size,
  fixedLetter,
  fixedNumber,
  currentLetter,
  currentNumber,
  onSubmit,
  onClose
}: {
  size: number;
  fixedLetter: string | null;
  fixedNumber: number | null;
  currentLetter: string | null;
  currentNumber: number | null;
  onSubmit: (letter: string | null, number: number | null) => void;
  onClose: () => void;
}) {
  const [letter, setLetter] = useState<string>(currentLetter || '');
  const [number, setNumber] = useState<number | ''>(currentNumber ?? '');

  const letters = Array.from({ length: size }, (_, i) => String.fromCharCode(65 + i));
  const numbers = Array.from({ length: size }, (_, i) => i + 1);

  const resolvedLetter = fixedLetter ?? (letter || null);
  const resolvedNumber = fixedNumber ?? (number !== '' ? number as number : null);
  const canSubmit = resolvedLetter !== null || resolvedNumber !== null;
  const hasBoth = resolvedLetter !== null && resolvedNumber !== null;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(resolvedLetter, resolvedNumber);
  };

  const handleClear = () => onSubmit(null, null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 pb-safe">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-1 text-center">Introduce valores</h3>
        <p className="text-sm text-gray-500 text-center mb-5">Puedes poner solo la letra, solo el número, o los dos</p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Letra
            {fixedLetter && <span className="ml-2 text-xs text-amber-700">(dada por el puzzle)</span>}
          </label>
          {fixedLetter ? (
            <div className="w-full px-4 py-3 rounded-lg text-lg font-bold text-amber-800"
              style={{ backgroundColor: '#fde68a', border: '3px solid #b45309' }}>
              {fixedLetter}
            </div>
          ) : (
            <select
              value={letter}
              onChange={(e) => setLetter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-800 text-lg"
            >
              <option value="">— (dejar vacío)</option>
              {letters.map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Número
            {fixedNumber !== null && <span className="ml-2 text-xs text-amber-700">(dado por el puzzle)</span>}
          </label>
          {fixedNumber !== null ? (
            <div className="w-full px-4 py-3 rounded-lg text-lg font-bold text-amber-800"
              style={{ backgroundColor: '#fde68a', border: '3px solid #b45309' }}>
              {fixedNumber}
            </div>
          ) : (
            <select
              value={number}
              onChange={(e) => setNumber(e.target.value ? parseInt(e.target.value) : '')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-800 text-lg"
            >
              <option value="">— (dejar vacío)</option>
              {numbers.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {hasBoth ? 'Confirmar' : 'Guardar parcial'}
          </button>
          <button
            onClick={handleClear}
            className="px-4 py-3 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
            title="Borrar celda"
          >
            Borrar
          </button>
          <button
            onClick={onClose}
            className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// Contar celdas llenas
function countFilled(board: Cell[][]): number {
  let count = 0;
  for (const row of board) {
    for (const cell of row) {
      if (cell.letter !== null && cell.number !== null) {
        count++;
      }
    }
  }
  return count;
}
